import React, { useEffect, useState } from 'react';
import { approveWorkSession, rejectWorkSession } from '../../../api/workSessionsApi';
import { toast } from 'react-toastify';
import ConfirmModal from '../confirm/ConfirmModal';
import ProgressoSubtarefas from '../../progresso-subtarefas/ProgressoSubtarefas';
import './ApproveSLAModal.css';
import '../../../shared/ui/modal-close-button.css'; // X padrão do sistema

interface SLASession {
  id: string;
  collaboratorName: string;
  esteiraId?: string;
  sector: string;
  durationInSeconds: number;
  totalPausedSeconds?: number;
  startedAt?: string;
  endedAt?: string;
}

interface PendingSLACar {
  carId: string;
  carPlate: string;
  carModel: string;
  carEsteiraId?: string;
  carStatus?: string;
  carPendingStatus?: string | null;
  carStatusApprovalStatus?: string | null;
  sessions: SLASession[];
}

interface ApproveSLAModalProps {
  isOpen: boolean;
  car: PendingSLACar | null;
  onClose: () => void;
  onUpdated: () => void;
}

const formatDuration = (seconds: number): string => {
  if (!seconds || seconds <= 0) return '-';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return h > 0 ? `${h}h ${m}min` : `${m}min`;
};

const ApproveSLAModal: React.FC<ApproveSLAModalProps> = ({ isOpen, car, onClose, onUpdated }) => {
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [loading, setLoading] = useState(false);
  // Confirmação explicativa antes de aprovar/reprovar (decisão de 07/2026).
  const [confirmData, setConfirmData] = useState<{ message: string; onConfirm: () => void } | null>(null);
  // Cópia local das sessões para remover em tempo real ao aprovar/reprovar.
  const [sessions, setSessions] = useState<SLASession[]>(car?.sessions ?? []);

  useEffect(() => {
    setSessions(car?.sessions ?? []);
  }, [car]);

  /**
   * at-89: esteiras DISTINTAS das sessões em aprovação. Sessão sem `esteiraId` (registro legado)
   * fica de fora — sem o id não dá para restringir, e mostrar o veículo inteiro seria
   * exatamente o que o filtro existe para evitar.
   */
  const esteirasEmAprovacao = Array.from(
    new Set(sessions.map((s) => s.esteiraId).filter((id): id is string => Boolean(id))),
  );

  // Remove a sessão da lista; fecha o modal quando não sobrar nenhuma.
  const removeSession = (sessionId: string) => {
    setSessions((prev) => {
      const next = prev.filter((s) => s.id !== sessionId);
      if (next.length === 0) onClose();
      return next;
    });
  };

  // Reflete resoluções vindas de outros clientes via WebSocket.
  useEffect(() => {
    const handleResolved = (e: Event) => {
      const detail = (e as CustomEvent).detail as { carId?: string; sessionId?: string };
      if (detail?.sessionId) removeSession(detail.sessionId);
    };
    window.addEventListener('work-session:resolved', handleResolved);
    return () => window.removeEventListener('work-session:resolved', handleResolved);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!isOpen || !car) return null;

  // Este modal só aprova as HORAS (não encadeia a aprovação de mudança de
  // esteira): com esteira sugerida pendente, o carro não é movido aqui.
  const carHasPendingEsteira = Boolean(car.carPendingStatus && car.carStatusApprovalStatus === 'PENDING');

  const buildApproveMessage = (session: SLASession) => {
    const collaborator = session.collaboratorName || 'Colaborador';
    const effects: string[] = [];
    if (carHasPendingEsteira) {
      effects.push(`o veículo ${car.carPlate} NÃO será movido — a mudança para a esteira ${car.carPendingStatus} solicitada pelo colaborador continuará pendente e deverá ser aprovada ou rejeitada separadamente`);
      effects.push('o colaborador responsável será mantido');
    } else if (session.esteiraId && car.carEsteiraId === session.esteiraId) {
      effects.push(`o veículo ${car.carPlate} será movido para Aguardando Próxima Etapa`);
      effects.push('o colaborador responsável será desvinculado');
    } else {
      effects.push(`o veículo ${car.carPlate} permanecerá na esteira ${car.carStatus || 'atual'} (ele já foi movido após esta atividade) — somente as horas serão aprovadas`);
    }
    return `Ao aprovar as horas de ${collaborator}: ${effects.join('; ')}. Tem certeza de que deseja aprovar?`;
  };

  const buildRejectMessage = (session: SLASession) => {
    const collaborator = session.collaboratorName || 'Colaborador';
    let message =
      `A reprovação das horas de ${collaborator} fará com que o veículo ${car.carPlate} permaneça na esteira ` +
      `${car.carStatus || 'atual'} e o colaborador responsável seja desvinculado. ${collaborator} será notificado da reprovação` +
      `${rejectionReason.trim() ? ' com a justificativa informada' : ''}.`;
    if (carHasPendingEsteira) {
      message += ` Atenção: a mudança para a esteira ${car.carPendingStatus} solicitada pelo colaborador continuará pendente e deverá ser aprovada ou rejeitada separadamente.`;
    }
    return `${message} Tem certeza de que deseja reprovar?`;
  };

  const executeApprove = async (sessionId: string) => {
    setLoading(true);
    try {
      await approveWorkSession(sessionId);
      toast.success('Horas aprovadas com sucesso.');
      removeSession(sessionId);
      onUpdated();
    } catch {
      toast.error('Erro ao aprovar horas.');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = (session: SLASession) => {
    setConfirmData({
      message: buildApproveMessage(session),
      onConfirm: () => {
        setConfirmData(null);
        executeApprove(session.id);
      },
    });
  };

  const executeReject = async (sessionId: string) => {
    setLoading(true);
    try {
      await rejectWorkSession(sessionId, rejectionReason || undefined);
      toast.success('Horas reprovadas. Colaborador será notificado.');
      setRejectingId(null);
      setRejectionReason('');
      removeSession(sessionId);
      onUpdated();
    } catch {
      toast.error('Erro ao reprovar horas.');
    } finally {
      setLoading(false);
    }
  };

  const handleRejectConfirm = () => {
    if (!rejectingId) return;
    const session = sessions.find((s) => s.id === rejectingId);
    if (!session) return;
    setConfirmData({
      message: buildRejectMessage(session),
      onConfirm: () => {
        setConfirmData(null);
        executeReject(session.id);
      },
    });
  };

  return (
    <div className="approve-sla-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="approve-sla-modal">
        <button
          type="button"
          onClick={onClose}
          className="close-button"
          aria-label="Fechar"
        >
          &times;
        </button>
        <div className="approve-sla-header">
          <p className="approve-sla-subtitle">
            Aprovação de horas
          </p>
          <h2 className="approve-sla-title">
            {car.carPlate} — {car.carModel}
          </h2>
        </div>

        {/* at-89: o que já foi feito — restrito às esteiras das sessões em aprovação.
            ⚠️ O filtro é a regra aqui (decisão do Victor, 03/09/2026): a aprovação é sobre UMA
            etapa, e listar as sub-tarefas das outras mistura serviço que ainda nem começou com
            o que está sendo julgado. Fica ANTES dos cards e uma vez só, com as esteiras
            DISTINTAS — dois colaboradores na mesma etapa veriam a lista duplicada. Some quando
            a etapa não tem sub-tarefas. */}
        {esteirasEmAprovacao.length > 0 && (
          <ProgressoSubtarefas carId={car.carId} apenasEsteiraIds={esteirasEmAprovacao} />
        )}

        {sessions.map((session) => (
          <div key={session.id} className="approve-sla-session-card">
            <div className="approve-sla-grid">
              <div className="approve-sla-grid-item">
                <p className="approve-sla-label">Colaborador</p>
                <p className="approve-sla-value">{session.collaboratorName}</p>
              </div>
              <div className="approve-sla-grid-item">
                <p className="approve-sla-label">Esteira</p>
                <p className="approve-sla-value">{session.sector}</p>
              </div>
              <div className="approve-sla-grid-item">
                <p className="approve-sla-label">Horas trabalhadas</p>
                <p className="approve-sla-value">
                  {formatDuration(session.durationInSeconds)}
                </p>
              </div>
              {(session.totalPausedSeconds ?? 0) > 0 && (
                <div className="approve-sla-grid-item">
                  <p className="approve-sla-label">Tempo pausado</p>
                  <p className="approve-sla-value" style={{ color: 'var(--tema-texto-3, #888)' }}>
                    {formatDuration(session.totalPausedSeconds ?? 0)}
                  </p>
                </div>
              )}
            </div>

            {rejectingId === session.id ? (
              <div>
                <textarea
                  placeholder="Motivo da reprovação (opcional)"
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  rows={2}
                  className="approve-sla-textarea"
                />
                <div className="approve-sla-actions">
                  <button
                    disabled={loading}
                    onClick={handleRejectConfirm}
                    className="approve-sla-btn btn-confirm-rejection"
                  >
                    Confirmar reprovação
                  </button>
                  <button
                    disabled={loading}
                    onClick={() => { setRejectingId(null); setRejectionReason(''); }}
                    className="approve-sla-btn btn-cancel"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            ) : (
              <div className="approve-sla-actions">
                <button
                  disabled={loading}
                  onClick={() => handleApprove(session)}
                  className="approve-sla-btn btn-approve"
                >
                  Aprovar horas
                </button>
                <button
                  disabled={loading}
                  onClick={() => setRejectingId(session.id)}
                  className="approve-sla-btn btn-reject"
                >
                  Reprovar
                </button>
              </div>
            )}
          </div>
        ))}

      </div>

      {confirmData && (
        <div onClick={(e) => e.stopPropagation()}>
          <ConfirmModal
            isOpen
            message={confirmData.message}
            confirmLabel="Sim, tenho certeza"
            onConfirm={confirmData.onConfirm}
            onCancel={() => setConfirmData(null)}
          />
        </div>
      )}
    </div>
  );
};

export default ApproveSLAModal;
